import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Container,
  Divider,
  Alert,
} from '@mui/material';
import Signature from '../components/Signature';
import PhotoCapture from '../components/PhotoCapture';
import PersonalInfo from '../components/PersonalInfo';
import Terms from '../components/Terms';
import LoginDialog from '../components/LoginDialog';
import { getOperatorData, getWaiverTemplate } from '../api';

const WaiverPage = () => {  const navigate = useNavigate();
  const location = useLocation(); // location object used in useCallback for fetchWaiverData
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Refs for component validation
  const personalInfoRef = useRef(null);
  const photoRefs = useRef({});
  const termsRef = useRef(null);
  const signatureRef = useRef(null);
  
  // Validation state
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [birthdate, setBirthdate] = useState(null);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [hasAgreedToElectronic, setHasAgreedToElectronic] = useState(false);
  
  // Dynamic photos and signature state
  const [formData, setFormData] = useState({});
  
    // Waiver template with new format
  const [waiverTemplate, setWaiverTemplate] = useState({
    id: null,
    operator_id: null,
    terms: null,
    waiver_logo: null,
    age_of_majority: 18,
    elements: []
  });  // Add userId state
  const [userId, setUserId] = useState('');
  // Add LoginDialog state
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');  // Add state to track if this is from reservation flow
  // This is determined by checking if reservationParams exists in localStorage
  const [isFromReservation, setIsFromReservation] = useState(!!localStorage.getItem('reservationParams'));
  
  // Add state to explicitly track if this should show payment flow
  // This is determined by the 'payment_flow' URL parameter
  const [showPaymentFlow, setShowPaymentFlow] = useState(false);
  
  // Separate function to fetch waiver data, memoized with useCallback
  const fetchWaiverData = useCallback((finalUserId = null) => {
    // Get parameters from URL or localStorage
    const queryParams = new URLSearchParams(location.search);
    
    // Check for operator ID in URL or localStorage
    let operatorParam = queryParams.get('operator');
    if (!operatorParam) {
      const storedOperator = localStorage.getItem('operator');
      if (storedOperator) {
        try {
          // Try to parse as JSON in case it's the full operator object
          const operatorObj = JSON.parse(storedOperator);
          operatorParam = operatorObj.id || operatorObj.operator_id;
        } catch (e) {
          // If it's not JSON, use it directly (it's probably just the ID)
          operatorParam = storedOperator;
        }
      }
    }
    const fleetParam = queryParams.get('fleet_id') || localStorage.getItem('fleet_id');      // Detect if this waiver was called from reservation flow or externally
    // ONLY if the reservationParams exist in localStorage
    const reservationParams = localStorage.getItem('reservationParams');
    const isFromResLocal = !!reservationParams; // Use local var to avoid conflict with state
    
    // Store the waiver source for the complete page to use
    localStorage.setItem('waiverSource', isFromResLocal ? 'reservation' : 'external');
    
    // Set state based on the actual presence of reservation parameters in localStorage
    setIsFromReservation(isFromResLocal); // Uses setIsFromReservation
    
    setLoading(true);    // Uses setLoading
    // Fetch operator data if we have an operator ID
    const operatorDataPromise = operatorParam 
      ? getOperatorData(operatorParam)
          .then((operatorResponse) => {
            // Store the full operator response to match behavior in reservations.js
            localStorage.setItem('operator', JSON.stringify(operatorResponse));
          })
          .catch(err => {
            console.error("Failed to fetch operator data:", err);
            setError("Failed to load operator information"); // Uses setError
          })
      : Promise.resolve();    // Fetch waiver template with whatever parameters we have - making sure to pass only the ID
    const waiverTemplatePromise = getWaiverTemplate(operatorParam, fleetParam, finalUserId)
      .then(templateData => {
        console.log('getWaiverTemplate returned:', templateData);
        if (templateData) {
          setWaiverTemplate(templateData); // Uses setWaiverTemplate
        }
      })
      .catch(err => {
        console.error('Failed to fetch waiver template:', err);
        setError('Failed to load waiver template. Please try again or contact support.'); // Uses setError
      });
      // Wait for both promises to complete
    Promise.all([operatorDataPromise, waiverTemplatePromise])
      .finally(() => {
        setLoading(false); // Uses setLoading
      });
  }, [location.search, setIsFromReservation, setLoading, setError, setWaiverTemplate]); // Dependencies for useCallback
  
  useEffect(() => {
    // Get parameters from URL or localStorage
    const queryParams = new URLSearchParams(location.search);
      // Check if this is from reservation flow by having reservationParams in localStorage
    const reservationParamsString = localStorage.getItem('reservationParams');
    
    console.log('=== Waiver Page Load Debug ===');
    console.log('URL search params:', location.search);
    console.log('reservationParamsString exists:', !!reservationParamsString);
    console.log('reservationParamsString content:', reservationParamsString);
    
      // Only consider it from reservation flow if reservationParams exists in localStorage
    setIsFromReservation(!!reservationParamsString);
      // Extract user_id and phone from URL
    const userIdParam = queryParams.get('user_id');
    const phoneParam = queryParams.get('phone');
    
    // Check for explicit payment flow parameter
    const paymentFlowParam = queryParams.get('payment_flow');
    setShowPaymentFlow(paymentFlowParam === 'true');
    
    console.log('userIdParam:', userIdParam);
    console.log('phoneParam:', phoneParam);
    console.log('paymentFlowParam:', paymentFlowParam);
    console.log('Setting showPaymentFlow to:', paymentFlowParam === 'true');
    
    setUserId(userIdParam || '');
    if (phoneParam) {
      setPhoneNumber(phoneParam);
    }
    
    // For validation purposes, check URL parameters
    const operatorParam = queryParams.get('operator');
    const fleetParam = queryParams.get('fleet_id');    // Clear reservationParams if this appears to be a direct access
    // Direct access is indicated by having user_id in URL but NOT when payment_flow=true
    // because reservation flow doesn't pass user_id in URL, it gets it from localStorage
    if (userIdParam && paymentFlowParam !== 'true') {
      console.log('🔍 Direct access with user_id detected (not payment flow), clearing any stale reservationParams');
      console.log('Before clearing - reservationParams:', localStorage.getItem('reservationParams'));
      localStorage.removeItem('reservationParams');
      setIsFromReservation(false);
      console.log('After clearing - isFromReservation set to false');
    } else if (userIdParam && paymentFlowParam === 'true') {
      console.log('🔍 Payment flow access detected with user_id, preserving reservationParams');
    }// Validation: If no user_id, we need phone number and at least one of operator/fleet_id IN THE URL
    if (!userIdParam) {
      if (!phoneParam) {
        setError('Phone number is required when user ID is not provided. Please include a phone parameter in the URL.');
        return;
      }
        // When using phone parameter, operator or fleet_id MUST be provided in URL, not localStorage
      if (!operatorParam && !fleetParam) {
        setError('Either operator ID or fleet ID is required when user ID is not provided. Please include operator or fleet_id parameter in the URL.');
        return;
      }
      // Clear any previous errors since validation passed
      setError('');
      
      // Show login dialog to get user authentication
      setIsLoginDialogOpen(true);
      return; // Don't fetch waiver template yet, wait for user authentication
    }
    
    // Get userId from local storage if available and not provided in URL
    let finalUserId = userIdParam;
    if (!finalUserId) {
      const userDataString = localStorage.getItem('user');
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          finalUserId = userData.id || userData.user_id;
          setUserId(finalUserId || '');
        } catch (error) {
          console.error("Error parsing user data from localStorage:", error);
        }
      }
    }
    
    // Fetch data if we have a userId
    if (finalUserId) {
      fetchWaiverData(finalUserId);
    }
  }, [location.search, fetchWaiverData]); // Added fetchWaiverData to dependencies

  // Update document title with fleet account name
  useEffect(() => {
    const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
    if (fleet.account_name) {
      document.title = fleet.account_name;
    } else {
      document.title = 'Roamr Reservations'; // fallback title
    }
  }, []);

  // Debug waiverTemplate
  useEffect(() => {
    // Template updated - ready for rendering
  }, [waiverTemplate]);
  
  // Update form data when a field changes
  const handleFormDataChange = (elementId, value) => {
    setFormData(prevData => ({
      ...prevData,
      [elementId]: value
    }));
  };  const validateForm = () => {
    // Check if we have user context first
    if (!userId) {
      setError('Please complete phone verification first.');
      return false;
    }
    
    // Check if we have form elements at all
    if (!waiverTemplate.elements || waiverTemplate.elements.length === 0) {
      setError('No form elements available. Please contact support.');
      return false;
    }
    
    // Clear any previous errors
    setError('');
    
    // Track the first error element ID for scrolling
    let firstErrorElementId = null;
    let firstErrorElementType = null;
    
    // Use the elements array to validate each component
    for (const element of waiverTemplate.elements) {
      let validationError = null;
      
      switch (element.type) {
        case 'personal_info':
          // Use the component's own validation method if available
          if (personalInfoRef.current && personalInfoRef.current.validate) {
            validationError = personalInfoRef.current.validate();
          } else {
            // Fallback to old validation method
            // validationError = validatePersonalInfo(element);
          }
          break;
        case 'photo':
          // Use the component's own validation method if available
          if (photoRefs.current[element.id] && photoRefs.current[element.id].validate) {
            validationError = photoRefs.current[element.id].validate();          } else {
            // Fallback to old validation method
            validationError = validatePhoto(element);
          }
          break;
        case 'terms':
          // Use the component's own validation method if available
          if (termsRef.current && termsRef.current.validate) {
            validationError = termsRef.current.validate();
          } else {
            // Fallback to old validation method
            validationError = validateTerms(element);
          }
          break;
        case 'signature':
          // Use the component's own validation method if available
          if (signatureRef.current && signatureRef.current.validate) {
            validationError = signatureRef.current.validate();
          } else {
            // Fallback to old validation method
            validationError = validateSignature(element);
          }
          break;
        default:
          // Skip unknown element types
          break;
      }
      
      if (validationError) {
        setError(validationError);
        // Store the first error element info to scroll to
        if (!firstErrorElementId) {
          firstErrorElementId = element.id;
          firstErrorElementType = element.type;
        }
      }
    }
    
    // If there was an error, scroll to the first error field
    if (firstErrorElementId && firstErrorElementType) {
      // Find the element by its data attributes
      const errorElement = document.querySelector(`[data-element-type="${firstErrorElementType}"][data-element-id="${firstErrorElementId}"]`);
      
      if (errorElement) {
        // Scroll with some offset to show the element nicely
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Add a small delay then scroll up slightly to account for fixed headers
        setTimeout(() => {
          window.scrollBy({ top: -100, behavior: 'smooth' });
        }, 300);
      } else {
        // Fallback: scroll to top if we can't find the specific element
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      return false;
    }
    
    return true;
  };

  const validatePhoto = (element) => {
    // Component validation is preferred
    console.warn("Using fallback validation for PhotoCapture");
    
    if (!formData[element.id]) {
      return `Please provide a photo for ${element.name}`;
    }
    return null;
  };
  
  const validateTerms = (element) => {
    // Component validation is preferred
    console.warn("Using fallback validation for Terms");
    
    if (!hasAgreed) {
      return `You must agree to the terms and conditions`;
    }
    
    if (!hasAgreedToElectronic) {
      return `You must consent to use electronic records and signatures`;
    }
    
    return null;
  };
  
  const validateSignature = (element) => {
    // Component validation is preferred
    console.warn("Using fallback validation for Signature");
    
    if (!formData[element.id]) {
      return `Please provide your signature for ${element.name || 'the form'}`;
    }
    return null;
  };  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setShowValidationErrors(true);
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const submissionData = waiverTemplate.elements.map(element => {
        let value = null;
        switch (element.type) {
          case 'personal_info':
            value = {
              firstName,
              lastName,
              email,
              birthdate: birthdate ? birthdate.toISOString().split('T')[0] : null
            };
            break;
          case 'photo':
            value = formData[element.id] || null;
            break;
          case 'terms':
            value = {
              hasAgreed,
              hasAgreedToElectronic
            };
            break;
          case 'signature':
            value = formData[element.id] || null;
            break;
          default:
            value = formData[element.id] || null;
        }
        return {
          id: element.id,
          type: element.type,
          value
        };
      });
      
      console.log("Waiver form data:", submissionData);

      // Use userId from state
      if (!userId) {
        throw new Error('User ID not found in URL. Please log in again.');
      }
      
      // Import submitWaiver dynamically to avoid circular dependency
      const { submitWaiver } = await import('../api');
      await submitWaiver(userId, submissionData);
      setSuccess(true);
        // Mark waiver as completed for this user
      localStorage.setItem(`waiverCompleted_${userId}`, 'true');
        // Handle different flows after waiver submission
      const reservationParamsString = localStorage.getItem('reservationParams');
      
      // Log the current state for debugging
      console.log('=== Waiver Submission Flow Debug ===');
      console.log('showPaymentFlow:', showPaymentFlow);
      console.log('isFromReservation:', isFromReservation);
      console.log('reservationParamsString exists:', !!reservationParamsString);
      console.log('reservationParamsString:', reservationParamsString);
      console.log('Current URL search params:', location.search);
      
      // Check if payment flow is explicitly requested via URL parameter
      if (showPaymentFlow) {
        console.log('🔄 Taking showPaymentFlow path (explicit payment_flow=true in URL)');
        // Payment flow explicitly requested - proceed to payment
        try {
          // If we have reservation params from localStorage, use them
          if (reservationParamsString) {
            console.log('✅ Found reservationParams in localStorage, proceeding to payment');
            const reservationParams = JSON.parse(reservationParamsString);
            const { params, stripe_pk } = reservationParams;
            console.log('Payment params:', params);
            console.log('Stripe PK exists:', !!stripe_pk);            
            // Import required functions
            const { createCheckoutSession } = await import('../api');
            const { loadStripe } = await import('@stripe/stripe-js');
            
            console.log('📞 Creating checkout session...');
            // Create checkout session and redirect to payment
            const session = await createCheckoutSession(params);
            console.log('✅ Checkout session created:', session.id);
            
            const stripe = await loadStripe(stripe_pk);
            console.log('💳 Stripe loaded, redirecting to checkout...');
            
            const { error } = await stripe.redirectToCheckout({ sessionId: session.id });
            if (error) {
              console.error('❌ Stripe redirect error:', error);
              throw new Error('Failed to redirect to payment. Please try again.');
            }
          } else {
            console.log('❌ No reservationParams available but payment flow requested');
            // No reservation params available but payment flow requested
            setError('Waiver submitted successfully, but payment information not found. Please contact support.');
          }
        } catch (error) {
          console.error('❌ Failed to proceed to payment in showPaymentFlow path:', error);
          setError('Waiver submitted successfully, but failed to proceed to payment. Please contact support.');
        }
      } else if (isFromReservation && reservationParamsString) {
        console.log('🔄 Taking legacy isFromReservation path');
        // Legacy flow: from reservation, proceed to payment
        try {
          console.log('✅ Found reservationParams in localStorage (legacy path), proceeding to payment');
          const reservationParams = JSON.parse(reservationParamsString);
          const { params, stripe_pk } = reservationParams;
          console.log('Payment params (legacy):', params);
          console.log('Stripe PK exists (legacy):', !!stripe_pk);          
          // Import required functions
          const { createCheckoutSession } = await import('../api');
          const { loadStripe } = await import('@stripe/stripe-js');
          
          console.log('📞 Creating checkout session (legacy path)...');
          // Create checkout session and redirect to payment
          const session = await createCheckoutSession(params);
          console.log('✅ Checkout session created (legacy):', session.id);
          
          const stripe = await loadStripe(stripe_pk);
          console.log('💳 Stripe loaded (legacy), redirecting to checkout...');
          
          const { error } = await stripe.redirectToCheckout({ sessionId: session.id });
          if (error) {
            console.error('❌ Stripe redirect error (legacy):', error);
            throw new Error('Failed to redirect to payment. Please try again.');
          }
        } catch (error) {
          console.error('❌ Failed to proceed to payment (legacy path):', error);
          setError('Waiver submitted successfully, but failed to proceed to payment. Please contact support.');
        }
      } else if (isFromReservation) {
        console.log('🔄 Taking isFromReservation without reservationParams path');
        // From reservation but no reservation params
        setError('Waiver submitted successfully, but reservation data not found. Please contact support.');      } else {
        console.log('🔄 Taking external waiver flow path (redirect to completion page)');
        // External waiver flow - redirect to completion page
        // Include operator and fleet parameters for consistent branding
        const queryParams = new URLSearchParams(location.search);
        let operatorParam = queryParams.get('operator');
        if (!operatorParam) {
          const storedOperator = localStorage.getItem('operator');
          if (storedOperator) {
            try {
              const operatorObj = JSON.parse(storedOperator);
              operatorParam = operatorObj.id || operatorObj.operator_id;
            } catch (e) {
              operatorParam = storedOperator;
            }
          }
        }
        const fleetParam = queryParams.get('fleet_id') || localStorage.getItem('fleet_id');
        
        let completeUrl = `/waiver_complete?user_id=${userId}`;
        if (operatorParam) completeUrl += `&operator=${operatorParam}`;
        if (fleetParam) completeUrl += `&fleet_id=${fleetParam}`;
        
        console.log('🎯 Redirecting to completion page:', completeUrl);
        setTimeout(() => {
          navigate(completeUrl);
        }, 2000);
      }
    } catch (err) {
      console.error("Failed to submit waiver:", err);
      
      // Provide more specific error messages based on the error
      let errorMessage = "Failed to submit waiver. Please try again.";
      
      if (err.toString && err.toString().includes('413')) {
        errorMessage = "Your photos are too large. Please try uploading smaller images or compress them before submitting.";
      } else if (err.toString && err.toString().includes('400')) {
        errorMessage = "Invalid waiver data. Please check your entries and try again.";
      } else if (err.toString && err.toString().includes('500')) {
        errorMessage = "Server error occurred. Please try again later or contact support.";
      } else if (err.message) {
        // Use the specific error message from the API
        errorMessage = err.message;
      }
      
      // Clear success state and set error
      setSuccess(false);
      setError(errorMessage);
      
      // Scroll to top to make sure error is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });    } 
    finally {
      setLoading(false);
    }
  };

  // Render form elements based on the template
  const renderFormElements = () => {
    // If no user context yet (waiting for login), show helpful message
    if (!userId) {
      return (
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Enter your phone number to continue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please verify your phone number to access the waiver form
            </Typography>
          </Box>
        </Grid>
      );
    }

    // If we have user context but no form elements, show error
    if (!waiverTemplate.elements || waiverTemplate.elements.length === 0) {
      return (
        <Grid item xs={12}>
          <Typography variant="body1" align="center" color="error" sx={{ my: 4 }}>
            No form elements available. Please contact support.
          </Typography>
        </Grid>
      );
    }

    // Sort elements by sort_order
    const sortedElements = [...waiverTemplate.elements].sort((a, b) => a.sort_order - b.sort_order);
    
    return sortedElements.map(element => {
      switch (element.type) {
        case 'personal_info':
          return (
            <React.Fragment key={element.id}>
              <Grid item xs={12} data-element-type="personal_info" data-element-id={element.id}>
                <Typography variant="h6" gutterBottom>
                  {element.name}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
                <Grid item xs={12}>                <PersonalInfo
                  ref={personalInfoRef}
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  birthdate={birthdate}
                  onFirstNameChange={setFirstName}
                  onLastNameChange={setLastName}
                  onEmailChange={setEmail}
                  onBirthdateChange={setBirthdate}
                  disabled={loading || success}
                  minAge={waiverTemplate.age_of_majority || 18}
                  instructionText={element.instruction_text}
                  showValidationErrors={showValidationErrors}
                  onValidationChange={(isValid) => {
                    // Only clear validation errors, not submission errors
                    if (isValid && error && !error.includes('Failed to submit') && !error.includes('too large') && !error.includes('Server error')) {
                      setError('');
                    }
                  }}
                />
              </Grid>
            </React.Fragment>
          );
          
        case 'photo':
          return (
            <React.Fragment key={element.id}>
              <Grid item xs={12} data-element-type="photo" data-element-id={element.id}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {element.name}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
                <Grid item xs={12}>                <PhotoCapture
                  ref={(el) => photoRefs.current[element.id] = el}
                  onImageCapture={(image) => handleFormDataChange(element.id, image)}
                  label={element.name}
                  required={true}
                  aspectRatio="3:4"
                  maxSize={1572864}  // 1.5MB
                  allowedTypes={["image/jpeg", "image/png", "image/heic"]}
                  showValidationErrors={showValidationErrors}
                  onValidationChange={(isValid) => {
                    // Only clear validation errors, not submission errors
                    if (isValid && error && !error.includes('Failed to submit') && !error.includes('too large') && !error.includes('Server error')) {
                      setError('');
                    }
                  }}
                />
              </Grid>
              
              {element.instruction_text && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    {element.instruction_text}
                  </Typography>
                </Grid>
              )}
            </React.Fragment>
          );
          
        case 'terms':
          return (
            <React.Fragment key={element.id}>
              <Grid item xs={12} data-element-type="terms" data-element-id={element.id}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {element.name}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
                <Grid item xs={12}>
                  <Terms
                    ref={termsRef}
                    title={element.name || "RENTAL AGREEMENT AND LIABILITY WAIVER"}
                    content={element.content || ""}
                    termsHtml={waiverTemplate.terms_html}
                    hasAgreed={hasAgreed}
                    onAgreementChange={setHasAgreed}
                    hasAgreedToElectronic={hasAgreedToElectronic}
                    onElectronicAgreementChange={setHasAgreedToElectronic}
                    disabled={loading || success}
                    instructionText={element.instruction_text}
                    showValidationErrors={showValidationErrors}
                    onValidationChange={(isValid) => {
                      // Only clear validation errors, not submission errors
                      if (isValid && error && !error.includes('Failed to submit') && !error.includes('too large') && !error.includes('Server error')) {
                        setError('');
                      }
                    }}
                  />
                </Grid>
            </React.Fragment>
          );
          
        case 'signature':
          return (
            <React.Fragment key={element.id}>              <Grid item xs={12} data-element-type="signature" data-element-id={element.id}>
                <Signature
                  ref={signatureRef}
                  onSignatureChange={(sig) => handleFormDataChange(element.id, sig)}
                  label={element.name}
                  required={true}
                  showValidationErrors={showValidationErrors}
                  onValidationChange={(isValid) => {
                    // Only clear validation errors, not submission errors
                    if (isValid && error && !error.includes('Failed to submit') && !error.includes('too large') && !error.includes('Server error')) {
                      setError('');
                    }
                  }}
                />
              </Grid>
              
              {element.instruction_text && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    {element.instruction_text}
                  </Typography>
                </Grid>
              )}
            </React.Fragment>
          );
          
        default:
          return null;
      }
    });
  };
  return (
    <div>      
      <AppBar position="static" style={{ backgroundColor: waiverTemplate.menu_bkg_color }}>
        <Toolbar>          
          <Box
            component="img"
            src={waiverTemplate.menu_logo}
            alt="Waiver Logo"
            sx={{
              padding: '8px',
              borderRadius: '4px',
              height: '75px',
              objectFit: 'contain',
              // Apply color filter to make the logo the desired color
              filter: waiverTemplate.menu_logo_color?.toLowerCase() === '#ffffff' || waiverTemplate.menu_logo_color?.toLowerCase() === '#fff' 
                ? 'brightness(0) saturate(100%) invert(1)' 
                : 'none',
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Paper elevation={3} sx={{ p: 3 }}>          
          <Typography variant="h4" gutterBottom align="center" sx={{ color: '#00B894', mb: 3 }}>
            {waiverTemplate.title || "Rental Waiver Form"}
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    border: '2px solid #f44336',
                    boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)',
                    '& .MuiAlert-message': {
                      fontWeight: 600,
                      fontSize: '1rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {(showPaymentFlow || isFromReservation)
                    ? 'Waiver submitted successfully! Redirecting to payment...' 
                    : 'Waiver submitted successfully! Redirecting...'
                  }
                </Alert>
              )}
                <Grid container spacing={3}>
                {renderFormElements()}
                
                {/* Only show submit button if we have user context and form elements */}
                {userId && waiverTemplate.elements && waiverTemplate.elements.length > 0 && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={loading || success}
                      sx={{
                        backgroundColor: '#00B894',
                        color: 'white',
                        borderRadius: '24px',
                        padding: '12px 32px',
                        fontWeight: 600,
                        boxShadow: '0 4px 8px rgba(0,184,148,0.3)',
                        width: '100%',
                        maxWidth: '300px',
                        '&:hover': {
                          backgroundColor: '#00A382',
                        },
                      }}                    >                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        // Use explicit payment_flow parameter if provided, otherwise fall back to existing logic
                        (() => {
                          const queryParams = new URLSearchParams(location.search);
                          
                          // If payment_flow parameter is explicitly set, use it
                          if (queryParams.get('payment_flow') !== null) {
                            return showPaymentFlow ? 'Continue to Payment' : 'Submit Waiver';
                          }
                          
                          // Fallback to existing logic for backward compatibility
                          const hasReservationParams = !!localStorage.getItem('reservationParams');
                          const shouldShowPayment = isFromReservation && hasReservationParams;
                          return shouldShowPayment ? 'Continue to Payment' : 'Submit Waiver';
                        })()
                      )}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          )}        </Paper>
      </Container>      <LoginDialog
        open={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
        title="Please verify your mobile number to proceed with the waiver"
        initialPhoneNumber={phoneNumber}
        successCallback={({ user_id }) => {
          console.log('Login successful, user_id:', user_id);
          setUserId(user_id);
          setIsLoginDialogOpen(false);
          // Now fetch the waiver data with the authenticated user ID
          fetchWaiverData(user_id);
        }}
      />
    </div>
  );
};

export default WaiverPage;